import { SurveyRow } from '../types';

// تعريف أنواع المقاييس المدعومة
export interface ScaleDefinition {
  name: string;
  values: Record<string, number>;
  normalizedRange: [number, number];
  type: 'agreement' | 'satisfaction' | 'quality' | 'performance' | 'effectiveness' | 'custom';
}

// معالج المقاييس المتعددة
export class MultiScaleProcessor {
  private scaleDefinitions: Record<string, ScaleDefinition> = {};
  private columnScaleMappings: Record<string, string> = {};

  constructor() {
    this.initializeDefaultScales();
  }

  // تهيئة المقاييس الافتراضية
  private initializeDefaultScales() {
    this.scaleDefinitions = {
      agreement: {
        name: 'مقياس الموافقة',
        values: {
          'موافق بشدة': 5,
          'موافق': 4,
          'محايد': 3,
          'غير موافق': 2,
          'غير موافق بشدة': 1
        },
        normalizedRange: [1, 5],
        type: 'agreement'
      },
      satisfaction: {
        name: 'مقياس الرضا',
        values: {
          'راضِ بشدة': 5,
          'راض': 4,
          'محايد': 3,
          'غير راض': 2,
          'غير راض بشدة': 1
        },
        normalizedRange: [1, 5],
        type: 'satisfaction'
      },
      quality: {
        name: 'مقياس الجودة',
        values: {
          'ممتاز': 5,
          'جيد جداً': 4,
          'محايد': 3,
          'ضعيف': 2,
          'ضعيف جداً': 1
        },
        normalizedRange: [1, 5],
        type: 'quality'
      },
      performance: {
        name: 'مقياس الأداء',
        values: {
          'ممتاز': 5,
          'جيد جداً': 4,
          'جيد': 3,
          'ضعيف': 2,
          'ضعيف جداً': 1
        },
        normalizedRange: [1, 5],
        type: 'performance'
      },
      effectiveness: {
        name: 'مقياس الفعالية',
        values: {
          'فعال جداً': 5,
          'فعال': 4,
          'متوسط الفعالية': 3,
          'قليل الفعالية': 2,
          'غير فعال': 1
        },
        normalizedRange: [1, 5],
        type: 'effectiveness'
      },
      usability: {
        name: 'مقياس سهولة الاستخدام',
        values: {
          'سهل جداً للاستخدام': 5,
          'سهل للاستخدام': 4,
          'محايد': 3,
          'غير سهل للاستخدام': 2,
          'ليس سهلاً للاستخدام على الإطلاق': 1
        },
        normalizedRange: [1, 5],
        type: 'custom'
      }
    };
  }

  // إضافة مقياس مخصص
  addCustomScale(scaleId: string, definition: Omit<ScaleDefinition, 'type'>) {
    this.scaleDefinitions[scaleId] = {
      ...definition,
      type: 'custom'
    };
  }

  // حذف مقياس مخصص
  removeCustomScale(scaleId: string) {
    if (this.scaleDefinitions[scaleId]?.type === 'custom') {
      delete this.scaleDefinitions[scaleId];
      // إزالة المرابطات المرتبطة بهذا المقياس
      Object.keys(this.columnScaleMappings).forEach(column => {
        if (this.columnScaleMappings[column] === scaleId) {
          delete this.columnScaleMappings[column];
        }
      });
    }
  }

  // تحديث مقياس مخصص
  updateCustomScale(scaleId: string, definition: Omit<ScaleDefinition, 'type'>) {
    if (this.scaleDefinitions[scaleId]?.type === 'custom') {
      this.scaleDefinitions[scaleId] = {
        ...definition,
        type: 'custom'
      };
    }
  }

  // الحصول على المقاييس المخصصة فقط
  getCustomScales(): Record<string, ScaleDefinition> {
    const customScales: Record<string, ScaleDefinition> = {};
    Object.entries(this.scaleDefinitions).forEach(([id, scale]) => {
      if (scale.type === 'custom') {
        customScales[id] = scale;
      }
    });
    return customScales;
  }

  // الحصول على المقاييس الافتراضية فقط
  getDefaultScales(): Record<string, ScaleDefinition> {
    const defaultScales: Record<string, ScaleDefinition> = {};
    Object.entries(this.scaleDefinitions).forEach(([id, scale]) => {
      if (scale.type !== 'custom') {
        defaultScales[id] = scale;
      }
    });
    return defaultScales;
  }

  // ربط عمود بمقياس معين
  mapColumnToScale(column: string, scaleId: string) {
    if (this.scaleDefinitions[scaleId]) {
      this.columnScaleMappings[column] = scaleId;
    }
  }

  // كشف نوع المقياس بناءً على القيم
  detectScaleType(values: string[]): string | null {
    const normalizedValues = values.map(v => v.toLowerCase().trim());
    
    // مقياس الموافقة
    if (this.hasKeywords(normalizedValues, ['موافق', 'غير موافق'])) {
      return 'agreement';
    }
    
    // مقياس الرضا
    if (this.hasKeywords(normalizedValues, ['راض', 'غير راض'])) {
      return 'satisfaction';
    }
    
    // مقياس الجودة
    if (this.hasKeywords(normalizedValues, ['ممتاز', 'جيد', 'ضعيف'])) {
      return 'quality';
    }
    
    // مقياس الأداء
    if (this.hasKeywords(normalizedValues, ['متميز', 'أداء'])) {
      return 'performance';
    }
    
    // مقياس الفعالية
    if (this.hasKeywords(normalizedValues, ['فعال', 'فعالية'])) {
      return 'effectiveness';
    }
    
    // مقياس سهولة الاستخدام
    if (this.hasKeywords(normalizedValues, ['سهل', 'سهولة', 'استخدام', 'للاستخدام'])) {
      return 'usability';
    }
    
    return null;
  }

  private hasKeywords(values: string[], keywords: string[]): boolean {
    return keywords.some(keyword => 
      values.some(val => val.includes(keyword))
    );
  }

  // تحويل النص إلى قيمة رقمية مع دعم التطابق الذكي
  convertTextToValue(text: string, scaleId?: string): number | null {
    const normalizedText = text.toLowerCase().trim();
    
    // إذا تم تحديد مقياس معين
    if (scaleId && this.scaleDefinitions[scaleId]) {
      const scale = this.scaleDefinitions[scaleId];
      return this.smartMatch(normalizedText, scale.values);
    }
    
    // البحث في جميع المقاييس
    for (const scale of Object.values(this.scaleDefinitions)) {
      const value = this.smartMatch(normalizedText, scale.values);
      if (value !== null) {
        return value;
      }
    }
    
    return null;
  }

  // التطابق الذكي للنصوص
  private smartMatch(input: string, scaleValues: Record<string, number>): number | null {
    // بحث دقيق
    for (const [key, value] of Object.entries(scaleValues)) {
      if (key.toLowerCase().trim() === input) {
        return value;
      }
    }
    
    // بحث جزئي
    for (const [key, value] of Object.entries(scaleValues)) {
      const normalizedKey = key.toLowerCase().trim();
      if (input.includes(normalizedKey) || normalizedKey.includes(input)) {
        return value;
      }
    }
    
    // بحث بالكلمات المرادفة
    const synonymMap = this.createSynonymMap();
    for (const [key, value] of Object.entries(scaleValues)) {
      const normalizedKey = key.toLowerCase().trim();
      if (this.matchWithSynonyms(input, normalizedKey, synonymMap)) {
        return value;
      }
    }
    
    return null;
  }

  // إنشاء خريطة المرادفات
  private createSynonymMap(): Record<string, string[]> {
    return {
      'بشدة': ['شدة', 'قوي', 'كبير', 'جداً', 'كثير'],
      'محايد': ['متوسط', 'عادي', 'معتدل', 'متوازن'],
      'ضعيف': ['سيء', 'قليل', 'منخفض', 'رديء'],
      'ممتاز': ['رائع', 'عظيم', 'مدهش', 'استثنائي'],
      'موافق': ['أوافق', 'أؤيد', 'أقبل', 'مؤيد'],
      'راض': ['راضي', 'مرضي', 'سعيد', 'مسرور'],
      'جيد': ['طيب', 'حسن', 'مقبول', 'لائق'],
      'سهل': ['يسير', 'بسيط', 'واضح', 'مفهوم', 'ميسر'],
      'صعب': ['عسير', 'معقد', 'غامض', 'مربك'],
      'للاستخدام': ['للاستعمال', 'في الاستخدام', 'في الاستعمال'],
      'الإطلاق': ['المطلق', 'نهائياً', 'كلياً', 'تماماً'],
      'فعال': ['مؤثر', 'نافع', 'مفيد', 'ناجح']
    };
  }

  // مطابقة مع المرادفات
  private matchWithSynonyms(input: string, key: string, synonymMap: Record<string, string[]>): boolean {
    for (const [keyword, synonyms] of Object.entries(synonymMap)) {
      if (key.includes(keyword)) {
        if (synonyms.some(synonym => input.includes(synonym))) {
          return true;
        }
      }
    }
    return false;
  }

  // تحويل البيانات مع معالجة المقاييس المتعددة
  processData(data: SurveyRow[], columnMappings: Record<string, string>): ProcessedSurveyData[] {
    return data.map(row => {
      const processedRow: ProcessedSurveyData = {
        originalData: row,
        processedValues: {},
        scaleInfo: {}
      };

      Object.entries(row).forEach(([column, value]) => {
        if (typeof value === 'string' && value.trim() !== '') {
          const scaleId = columnMappings[column];
          const numericValue = this.convertTextToValue(value, scaleId);
          
          if (numericValue !== null) {
            processedRow.processedValues[column] = numericValue;
            processedRow.scaleInfo[column] = {
              scaleId: scaleId || 'auto-detected',
              originalValue: value,
              convertedValue: numericValue,
              scaleName: this.scaleDefinitions[scaleId || '']?.name || 'مقياس تلقائي'
            };
          }
        }
      });

      return processedRow;
    });
  }

  // تطبيع القيم عبر مقاييس مختلفة (اختياري للمقارنة)
  normalizeValue(value: number, scaleId: string, targetRange: [number, number] = [1, 5]): number {
    const scale = this.scaleDefinitions[scaleId];
    if (!scale) return value;

    const [sourceMin, sourceMax] = scale.normalizedRange;
    const [targetMin, targetMax] = targetRange;
    
    // تطبيع القيمة
    const normalizedValue = ((value - sourceMin) / (sourceMax - sourceMin)) * (targetMax - targetMin) + targetMin;
    
    // تقريب إلى منزلة عشرية واحدة
    return Math.round(normalizedValue * 10) / 10;
  }

  // حساب إحصائيات متقدمة للمقاييس المتعددة
  calculateMultiScaleStatistics(processedData: ProcessedSurveyData[], column: string): MultiScaleStatistics {
    const values = processedData
      .map(row => row.processedValues[column])
      .filter(val => val !== undefined) as number[];

    if (values.length === 0) {
      return {
        count: 0,
        mean: 0,
        median: 0,
        mode: 0,
        standardDeviation: 0,
        variance: 0,
        range: [0, 0],
        distribution: {},
        scaleInfo: null
      };
    }

    const scaleInfo = processedData.find(row => row.scaleInfo[column])?.scaleInfo[column] || null;
    
    return {
      count: values.length,
      mean: this.calculateMean(values),
      median: this.calculateMedian(values),
      mode: this.calculateMode(values),
      standardDeviation: this.calculateStandardDeviation(values),
      variance: this.calculateVariance(values),
      range: [Math.min(...values), Math.max(...values)],
      distribution: this.calculateDistribution(values),
      scaleInfo
    };
  }

  // دوال حساب إحصائية مساعدة
  private calculateMean(values: number[]): number {
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  private calculateMedian(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 
      ? (sorted[mid - 1] + sorted[mid]) / 2 
      : sorted[mid];
  }

  private calculateMode(values: number[]): number {
    const frequency: Record<number, number> = {};
    values.forEach(val => {
      frequency[val] = (frequency[val] || 0) + 1;
    });
    
    let maxFreq = 0;
    let mode = values[0];
    Object.entries(frequency).forEach(([val, freq]) => {
      if (freq > maxFreq) {
        maxFreq = freq;
        mode = Number(val);
      }
    });
    
    return mode;
  }

  private calculateStandardDeviation(values: number[]): number {
    const mean = this.calculateMean(values);
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  }

  private calculateVariance(values: number[]): number {
    const mean = this.calculateMean(values);
    return values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  }

  private calculateDistribution(values: number[]): Record<number, number> {
    const distribution: Record<number, number> = {};
    values.forEach(val => {
      distribution[val] = (distribution[val] || 0) + 1;
    });
    return distribution;
  }

  // الحصول على معلومات المقياس
  getScaleInfo(scaleId: string): ScaleDefinition | null {
    return this.scaleDefinitions[scaleId] || null;
  }

  // الحصول على جميع المقاييس المعرفة
  getAllScales(): Record<string, ScaleDefinition> {
    return { ...this.scaleDefinitions };
  }
}

// تعريف أنواع البيانات
export interface ProcessedSurveyData {
  originalData: SurveyRow;
  processedValues: Record<string, number>;
  scaleInfo: Record<string, {
    scaleId: string;
    originalValue: string;
    convertedValue: number;
    scaleName: string;
  }>;
}

export interface MultiScaleStatistics {
  count: number;
  mean: number;
  median: number;
  mode: number;
  standardDeviation: number;
  variance: number;
  range: [number, number];
  distribution: Record<number, number>;
  scaleInfo: {
    scaleId: string;
    originalValue: string;
    convertedValue: number;
    scaleName: string;
  } | null;
}

// تصدير instance جاهز للاستخدام
export const multiScaleProcessor = new MultiScaleProcessor(); 